import type { ActionPayload, ActionType, IdentifyRequest } from '../contract/v1';
import { DEFAULT_FLUSH_BATCH_SIZE, DEFAULT_FLUSH_INTERVAL_MS, SDK_VERSION } from '../contract/v1';
import type {
  VeoClient,
  VeoClientEventListener,
  VeoClientEventName,
  VeoClientEvents,
  VeoConfig,
  VeoOrganization,
  VeoPlugin,
  VeoVisitor,
} from '../types';
import { hasDocument, hasWindow } from '../utils/safe-env';
import { uuidv7 } from '../utils/uuid';
import { resolveApiUrl } from './api-url';
import { Queue } from './queue';
import { Session } from './session';
import { createStorage, type StorageAdapter } from './storage';
import { Transport } from './transport';

const ORGANIZATION_ID_KEY = 'organization_id';
const END_USER_ID_KEY = 'end_user_id';
const IDENTIFY_SIG_KEY = 'identify_signature';

/**
 * `JSON.stringify` con claves ordenadas (recursivo) para que la firma de
 * identidad sea estable sin importar el orden en que la app arme el objeto
 * de traits/attributes. Sin esto, `{a,b}` y `{b,a}` darían firmas distintas
 * y el identify se reenviaría sin que nada haya cambiado.
 */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const entries = Object.keys(value as Record<string, unknown>)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stableStringify((value as Record<string, unknown>)[k])}`);
  return `{${entries.join(',')}}`;
}

/** Campos opcionales que cada API pública aporta al construir el evento. */
interface EnqueueInput {
  actionType: ActionType;
  actionName?: string;
  pageUrl?: string;
  pagePath?: string;
  pageTitle?: string;
  pageReferrer?: string;
  actionProperties?: Record<string, unknown>;
}

/**
 * Cliente principal del SDK. Expone la API pública (`identify`, `track`,
 * `pageview`, `reset`, `flush`, `use`). Toda la API salvo `flush()` es síncrona:
 * los eventos se encolan y la llamada retorna de inmediato.
 */
export class Client implements VeoClient {
  private readonly transport: Transport;
  private readonly storage: StorageAdapter;
  private readonly session: Session;
  private readonly queue: Queue<ActionPayload>;
  private readonly plugins: VeoPlugin[] = [];
  private currentOrganizationId: string | null = null;
  private readonly listeners: {
    [E in VeoClientEventName]: Set<VeoClientEventListener<E>>;
  } = {
    pageview: new Set(),
    identify: new Set(),
  };

  constructor(private readonly config: VeoConfig) {
    if (!config.apiKey) throw new Error('apiKey is required');

    this.storage = createStorage(config.storage ?? 'localStorage');
    this.session = new Session(this.storage);

    this.transport = new Transport({
      apiKey: config.apiKey,
      apiUrl: resolveApiUrl(config).replace(/\/$/, ''),
      sdkVersion: SDK_VERSION,
    });

    this.queue = new Queue<ActionPayload>({
      transport: this.transport,
      flushIntervalMs: config.flushInterval ?? DEFAULT_FLUSH_INTERVAL_MS,
      batchSize: config.flushBatchSize ?? DEFAULT_FLUSH_BATCH_SIZE,
      onError: (err) => {
        if (config.debug) console.error('[veo] flush error:', err);
      },
    });

    // Rehidratar identidad persistida.
    const persistedUserId = this.storage.get(END_USER_ID_KEY);
    if (persistedUserId) this.session.setEndUserId(persistedUserId);

    const persistedOrgId = this.storage.get(ORGANIZATION_ID_KEY);
    if (persistedOrgId) this.currentOrganizationId = persistedOrgId;

    this.setupUnloadHandlers();

    if (config.debug) {
      console.log('[veo] initialized', {
        anonymousId: this.session.anonymousId,
        sessionId: this.session.sessionId,
        endUserId: this.session.endUserId,
      });
    }
  }

  identify(payload: { visitor: VeoVisitor; organization?: VeoOrganization }): void {
    const { visitor, organization } = payload;

    // Idempotencia: si ya estamos identificados como este visitor y ni los
    // traits ni la organización cambiaron, no reenviamos. Esto evita el spam
    // de identify cuando la app lo llama en cada carga/navegación (un patrón
    // común y recomendado): el SDK absorbe la repetición en vez de generar un
    // evento `identify` y un POST /v1/identify por cada render.
    const signature = stableStringify({
      id: visitor.id,
      traits: visitor.traits ?? {},
      org: organization ? { id: organization.id, attributes: organization.attributes ?? {} } : null,
    });
    if (this.session.endUserId === visitor.id && this.storage.get(IDENTIFY_SIG_KEY) === signature) {
      if (this.config.debug) console.debug('[veo] identify: sin cambios, omitido');
      return;
    }

    // Capturar el anonymous previo ANTES de mutar la identidad, para el merge en backend.
    const previousAnonymousId =
      this.session.endUserId === null ? this.session.anonymousId : undefined;

    this.session.setEndUserId(visitor.id);
    if (organization) {
      this.currentOrganizationId = organization.id;
      this.storage.set(ORGANIZATION_ID_KEY, organization.id);
    }

    // 1. Upsert de traits/attributes en /v1/identify (fire-and-forget).
    const endUser: IdentifyRequest['endUser'] = {
      id: visitor.id,
      traits: visitor.traits ?? {},
      ...(previousAnonymousId !== undefined ? { anonymousId: previousAnonymousId } : {}),
    };
    const body: IdentifyRequest = {
      endUser,
      ...(organization
        ? { organization: { id: organization.id, attributes: organization.attributes ?? {} } }
        : {}),
    };
    void this.transport.send('/v1/identify', body).catch((err) => {
      if (this.config.debug) console.error('[veo] identify failed:', err);
    });

    // 2. Registrar el evento "identify" en el log de acciones.
    this.enqueue({
      actionType: 'identify',
      actionProperties: {
        traits: visitor.traits,
        organizationAttributes: organization?.attributes,
      },
    });

    this.storage.set(IDENTIFY_SIG_KEY, signature);

    this.emit('identify', { endUserId: visitor.id, sessionId: this.session.sessionId });
  }

  track(name: string, properties?: Record<string, unknown>): void {
    if (!name || typeof name !== 'string') {
      if (this.config.debug) console.error('[veo] track: name is required');
      return;
    }
    this.enqueue({
      actionType: 'track',
      actionName: name,
      ...(properties !== undefined ? { actionProperties: properties } : {}),
    });
  }

  pageview(path?: string): void {
    if (!hasDocument()) return;

    const url = window.location.href;
    const pagePath = path ?? window.location.pathname;

    this.enqueue({
      actionType: 'pageview',
      pageUrl: url,
      pagePath,
      pageTitle: document.title,
      pageReferrer: document.referrer,
    });

    this.emit('pageview', {
      url,
      path: pagePath,
      sessionId: this.session.sessionId,
      endUserId: this.session.endUserId,
    });
  }

  reset(): void {
    this.session.reset();
    this.currentOrganizationId = null;
    this.storage.remove(ORGANIZATION_ID_KEY);
    this.storage.remove(END_USER_ID_KEY);
    this.storage.remove(IDENTIFY_SIG_KEY);
  }

  flush(): Promise<void> {
    return this.queue.flush();
  }

  use(plugin: VeoPlugin): VeoClient {
    this.plugins.push(plugin);
    plugin.install(this);
    return this;
  }

  getEndUserId(): string | null {
    return this.session.endUserId;
  }

  getAnonymousId(): string {
    return this.session.anonymousId;
  }

  getSessionId(): string {
    return this.session.sessionId;
  }

  /**
   * Suscribe `listener` a `event`. Devuelve una función para desuscribir.
   * Los errores en listeners se logean en debug mode pero NO se propagan
   * (un plugin no debe poder romper a otro).
   */
  on<E extends VeoClientEventName>(event: E, listener: VeoClientEventListener<E>): () => void {
    this.listeners[event].add(listener);
    return () => {
      this.listeners[event].delete(listener);
    };
  }

  private emit<E extends VeoClientEventName>(event: E, payload: VeoClientEvents[E]): void {
    for (const listener of this.listeners[event]) {
      try {
        listener(payload);
      } catch (err) {
        if (this.config.debug) console.error(`[veo] listener for '${event}' failed:`, err);
      }
    }
  }

  /**
   * Construye el evento completo y lo agrega a la queue. Omite los campos
   * opcionales no definidos (exigido por `exactOptionalPropertyTypes`).
   * @internal
   */
  private enqueue(input: EnqueueInput): void {
    this.session.touch();

    const organizationId = this.currentOrganizationId ?? undefined;
    const pageUrl = input.pageUrl ?? (hasWindow() ? window.location.href : undefined);
    const pagePath = input.pagePath ?? (hasWindow() ? window.location.pathname : undefined);
    const pageTitle = input.pageTitle ?? (hasDocument() ? document.title : undefined);
    const pageReferrer = input.pageReferrer ?? (hasDocument() ? document.referrer : undefined);

    const event: ActionPayload = {
      actionId: uuidv7(),
      endUserId: this.session.effectiveUserId,
      sessionId: this.session.sessionId,
      actionType: input.actionType,
      occurredAt: new Date().toISOString(),
      ...(organizationId !== undefined ? { organizationId } : {}),
      ...(input.actionName !== undefined ? { actionName: input.actionName } : {}),
      ...(pageUrl !== undefined ? { pageUrl } : {}),
      ...(pagePath !== undefined ? { pagePath } : {}),
      ...(pageTitle !== undefined ? { pageTitle } : {}),
      ...(pageReferrer !== undefined ? { pageReferrer } : {}),
      ...(input.actionProperties !== undefined ? { actionProperties: input.actionProperties } : {}),
    };

    this.queue.add(event);
  }

  private setupUnloadHandlers(): void {
    if (!hasWindow()) return;

    window.addEventListener('pagehide', () => this.queue.flushBeacon());
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.queue.flushBeacon();
      }
    });
  }
}
